import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Form, Modal, Spinner } from 'react-bootstrap';
import moment from 'moment';
import classnames from 'classnames';
import { FaEnvelope, FaFileInvoiceDollar, FaFilePdf, FaPaperPlane, FaTimes, FaWhatsapp } from 'react-icons/fa';
import { FiHome } from 'react-icons/fi';
// PDF generation moved to backend - no longer needed
// import { jsPDF } from 'jspdf';
// import html2canvas from 'html2canvas';
import BusyOverlay from '../../components/BusyOverlay/BusyOverlay';
import { StatStrip, IdChip, Button, Select, Checkbox, Pill } from '../../components/EntityList';
import AssetSaleService from '../../services/assetSale.service';
import ContractService from '../../services/contract.service';
import GeneratedDebtService from '../../services/generatedDebts.service';
import BillService from '../../services/bill.service';
import AccountService from '../../services/account.service';
import AssetService from '../../services/asset.service';
import ConfigService from '../../services/settings.service';
import commissionService from '../../services/commission.service';
import debtConceptService from '../../services/debtConcept.service';
import { normalizeConceptName } from '../../utils/conceptMeta';
import { getError, showCurrency } from '../../utils';
import { isFacturacionEnabled } from '../../utils/featureFlags';
import facturacionService from '../../services/facturacion.service';
import FacturacionBalancePanel from '../../components/FacturacionBalancePanel/FacturacionBalancePanel';
import { useParams } from 'react-router-dom';
import { toast } from '../../toasts';
import { resolveDueCurrency, resolveBillCurrency, computeCommissionDeduction, commissionAppliesToCurrency, bucketTotals, bucketTotalsByAsset, mergeBucketResults, dueIsCollected, billChargesOwner, billChargesTenant, saldoLabel, buildBalanceCaption, resolveDebtPaymentTo } from './balanceTotals';

const getCommissionRateLabel = (commission) =>
	commission.percentage !== null && commission.percentage !== undefined
		? `${commission.percentage}%`
		: 'monto fijo';

const getCommissionLabel = (commission) => {
	if (commission.observations) return commission.observations;
	if (commission.isExternalSeller && commission.externalSellerName) return commission.externalSellerName;
	if (commission.seller?.name) return commission.seller.name;
	return 'Comisión';
};

const normalizePhone = (raw) => raw?.replace(/\D/g, '') || '';
const isValidPhone = (raw) => {
	const digits = normalizePhone(raw);
	return digits.length === 11 && digits.startsWith('598');
};
const isValidEmail = (raw) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((raw || '').trim());

function BalanceTableSkeleton({ type }) {
	const columns = type === 'account' ? 2 : 3;
	const rows = type === 'account' ? 9 : 8;

	return (
		<div className='tw:overflow-x-auto' aria-busy='true' aria-live='polite'>
			<table className='tw:w-full tw:border-collapse tw:tabular-nums'>
				<thead>
					<tr>
						{Array.from({ length: columns }, (_, index) => (
							<th
								key={index}
								className='tw:px-4 tw:py-3 tw:text-left tw:bg-surface-2 tw:border-b tw:border-border'
							>
								<span className='el-sk tw:block tw:h-[11px] tw:w-24' />
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{Array.from({ length: rows }, (_, rowIndex) => (
						<tr key={rowIndex} className='tw:border-b tw:border-border tw:last:border-b-0'>
							{Array.from({ length: columns }, (_, colIndex) => (
								<td key={colIndex} className='tw:px-4 tw:py-3'>
									<span
										className={classnames(
											'el-sk tw:block tw:h-[13px]',
											colIndex === columns - 1 ? 'tw:ml-auto tw:w-24' : rowIndex % 3 === 0 ? 'tw:w-3/5' : 'tw:w-4/5',
										)}
									/>
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

function Balance({ type, data }) {
	const params = useParams();
	const { id } = params;
	
	const [selectedMonth, setSelectedMonth] = useState('');
	const [monthOptions, setMonthOptions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [pdfLoading, setPdfLoading] = useState(false);
	const [error, setError] = useState('');
	const [balanceData, setBalanceData] = useState(null);
	const monthsInitialized = useRef(false);

	// Both default to true (today's behaviour: paid items count as accrued
	// income/expense). Unticking either excludes that side from the fetch.
	const [includePaidBills, setIncludePaidBills] = useState(true);
	const [includePaidDues, setIncludePaidDues] = useState(true);

	// Concept slugs treated as expense deductions in the balance split.
	// 'gasto-de-activo-fijo' is always kept as a legacy fallback (old data may
	// reference it even if the concept doc no longer exists or lost its flag).
	const [expenseConcepts, setExpenseConcepts] = useState(() => new Set(['gasto-de-activo-fijo']));
	// slug -> display name for fetched expense concepts (merged into getConceptLabel)
	const [expenseConceptNames, setExpenseConceptNames] = useState({});

	// Names which side collects the income, derived from the dues' source
	// paymentTo — one shared rule with the PDF instead of two implementations.
	const balanceCaption = useMemo(
		() => buildBalanceCaption(balanceData?.debtDues || [], expenseConcepts),
		[balanceData, expenseConcepts],
	);

	const [showEmailModal, setShowEmailModal] = useState(false);
	const [emailInput, setEmailInput] = useState('');
	const [emailSending, setEmailSending] = useState(false);
	const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
	const [phoneInput, setPhoneInput] = useState('');
	const [sharing, setSharing] = useState(false);
	const defaultPhoneRef = useRef(null);

	// Facturación electrónica (GotaBilling). facturacionAvailable flips to false
	// on the first 404 from requireFacturacion (feature off or unconfigured
	// server-side) so the button hides without extra round-trips.
	const [facturacionAvailable, setFacturacionAvailable] = useState(isFacturacionEnabled() && type !== 'debt');
	const [facturacionRecord, setFacturacionRecord] = useState(null);
	const [showFacturarModal, setShowFacturarModal] = useState(false);
	const [facturarPreview, setFacturarPreview] = useState(null);
	const [facturarPreviewError, setFacturarPreviewError] = useState('');
	const [facturarPreviewLoading, setFacturarPreviewLoading] = useState(false);
	const [facturarSending, setFacturarSending] = useState(false);
	const [facturacionRefreshing, setFacturacionRefreshing] = useState(false);

	// Get start date based on type
	const getStartDate = () => {
		if (!data) return null;
		if (type === 'assetSale') {
			return moment.utc(data.date);
		} else if (type === 'contract') {
			return moment.utc(data.periodInit);
		} else if (type === 'debt') {
			return moment.utc(data.date);
		} else if (type === 'account') {
			// For account, we'll calculate the earliest date from debts/contracts
			// This will be set in the useEffect when we fetch the data
			return null; // Will be calculated dynamically
		}
		return null;
	};

	// Get asset IDs for account type
	const getAssetIds = async () => {
		if (type === 'account' && data?._id) {
			try {
				const assetsResponse = await AssetService.listAssetsByAccount(data._id);
				const assets = assetsResponse.data || [];
				return assets.map(asset => (asset._id || asset).toString());
			} catch (err) {
				console.error('Error fetching assets:', err);
				return [];
			}
		}
		return null;
	};

	// Get asset ID for other types
	const getAssetId = () => {
		if (!data) return null;
		if (type === 'assetSale') {
			return data.asset?._id || data.asset;
		} else if (type === 'contract') {
			return data.property?._id || data.property;
		} else if (type === 'debt') {
			// For debt, get asset from contract or assetSale
			if (data.contract?.property) {
				return data.contract.property._id || data.contract.property;
			} else if (data.assetSale?.asset) {
				return data.assetSale.asset._id || data.assetSale.asset;
			}
		}
		return null;
	};

	// Fetch active concepts flagged treatAsExpense to drive the expense split
	useEffect(() => {
		let isMounted = true;
		const fetchExpenseConcepts = async () => {
			try {
				const { data } = await debtConceptService.list({ active: true });
				if (!isMounted) return;
				const keys = new Set(['gasto-de-activo-fijo']);
				const names = {};
				(data || []).forEach((doc) => {
					if (!doc || !doc.treatAsExpense) return;
					const key = doc.key || (doc.name ? normalizeConceptName(doc.name) : null);
					if (!key) return;
					keys.add(key);
					if (doc.name) names[key] = doc.name;
				});
				setExpenseConcepts(keys);
				setExpenseConceptNames(names);
			} catch (err) {
				// Keep the legacy fallback set on failure
				console.error('Error fetching debt concepts:', err);
			}
		};
		fetchExpenseConcepts();
		return () => {
			isMounted = false;
		};
	}, []);

	// Generate month options
	useEffect(() => {
		// Reset initialization flag when data/type changes
		monthsInitialized.current = false;
		
		const calculateStartDate = async () => {
			if (type === 'account' && data?._id) {
				// For account, find the earliest date from debts and contracts
				try {
					const [debtsResponse, contractsResponse] = await Promise.all([
						AccountService.getDebtsForAccountId(data._id),
						ContractService.getContractsForAccount(data._id),
					]);
					
					const debts = debtsResponse.data || [];
					const contracts = contractsResponse.data || [];
					
					const dates = [];
					debts.forEach(debt => {
						if (debt.date) dates.push(moment.utc(debt.date));
					});
					contracts.forEach(contract => {
						if (contract.periodInit) dates.push(moment.utc(contract.periodInit));
					});
					
					if (dates.length > 0) {
						const earliestDate = moment.min(dates);
						return earliestDate;
					}
					// Default to 1 year ago if no dates found
					return moment.utc().subtract(1, 'year');
				} catch (err) {
					console.error('Error calculating start date:', err);
					return moment.utc().subtract(1, 'year');
				}
			}
			return getStartDate();
		};

		const initializeMonths = async () => {
			const startDate = await calculateStartDate();
			if (!startDate) return;

			const currentDate = moment.utc();
			const months = [];
			let currentMonth = moment.utc(startDate).startOf('month');

			while (currentMonth.isSameOrBefore(currentDate, 'month')) {
				const monthValue = currentMonth.format('YYYY-MM');
				const monthLabel = currentMonth.format('MMMM YYYY');
				months.push({ value: monthValue, label: monthLabel });
				currentMonth.add(1, 'month');
			}

			setMonthOptions(months);
			if (months.length > 0 && !monthsInitialized.current) {
				setSelectedMonth(months[months.length - 1].value); // Set to current month by default
				monthsInitialized.current = true;
			}
		};

		initializeMonths();
	}, [data, type]);

	// Fetch balance data
	useEffect(() => {
		if (!selectedMonth || !data) return;

		const fetchBalanceData = async () => {
			setLoading(true);
			setError('');
			try {
				// Get start and end of selected month
				const monthStart = moment.utc(selectedMonth).startOf('month').format('YYYY-MM-DD');
				const monthEnd = moment.utc(selectedMonth).endOf('month').format('YYYY-MM-DD');

				// Fetch debts
				let debts = [];
				let assetIds = null; // Declare outside to use for bills later
				let allContracts = []; // Hoisted for commission fetching (account type)
				let allAssetSales = []; // Hoisted for commission fetching (account type)

				if (type === 'assetSale') {
					const debtsResponse = await AssetSaleService.getDebtsForAssetSale(id);
					debts = debtsResponse.data || [];
				} else if (type === 'contract') {
					const debtsResponse = await ContractService.getDebtsForContract(id);
					debts = debtsResponse.data || [];
				} else if (type === 'debt') {
					// For single debt, use the debt directly
					debts = [data];
				} else if (type === 'account') {
					// For account, get all debts from:
					// 1. Direct debts where account is owner
					// 2. Debts from AssetSales related to account's assets
					// 3. Debts from Contracts related to account's assets
					
					// Get all assets where account is owner (we'll use this later for bills too)
					assetIds = await getAssetIds();
					
					// Get direct debts
					const directDebtsResponse = await AccountService.getDebtsForAccountId(id);
					const directDebts = directDebtsResponse.data || [];
					
					// Get debts from AssetSales and Contracts for each asset
					const assetSaleDebts = [];
					const contractDebts = [];
					
					if (assetIds && assetIds.length > 0) {
						// Fetch AssetSales and Contracts for all assets in parallel
						const assetSalePromises = assetIds.map(assetId => 
							AssetSaleService.getSalesForProperty(assetId).catch(err => {
								console.error(`Error fetching AssetSales for asset ${assetId}:`, err);
								return { data: [] };
							})
						);
						const contractPromises = assetIds.map(assetId => 
							ContractService.getContractsForProperty(assetId).catch(err => {
								console.error(`Error fetching Contracts for asset ${assetId}:`, err);
								return { data: [] };
							})
						);
						
						const assetSalesResults = await Promise.all(assetSalePromises);
						const contractsResults = await Promise.all(contractPromises);
						
						// Get all AssetSales and Contracts (assign to hoisted vars for commission fetching)
						allAssetSales = assetSalesResults.flatMap(result => result.data || []);
						allContracts = contractsResults.flatMap(result => result.data || []);
						
						// Get debts from each AssetSale
						const assetSaleDebtPromises = allAssetSales.map(assetSale => 
							AssetSaleService.getDebtsForAssetSale(assetSale._id || assetSale).catch(err => {
								console.error(`Error fetching debts for AssetSale ${assetSale._id}:`, err);
								return { data: [] };
							})
						);
						
						// Get debts from each Contract
						const contractDebtPromises = allContracts.map(contract => 
							ContractService.getDebtsForContract(contract._id || contract).catch(err => {
								console.error(`Error fetching debts for Contract ${contract._id}:`, err);
								return { data: [] };
							})
						);
						
						const assetSaleDebtResults = await Promise.all(assetSaleDebtPromises);
						const contractDebtResults = await Promise.all(contractDebtPromises);
						
						assetSaleDebts.push(...assetSaleDebtResults.flatMap(result => result.data || []));
						contractDebts.push(...contractDebtResults.flatMap(result => result.data || []));
					}
					
					// Combine all debts
					debts = [...directDebts, ...assetSaleDebts, ...contractDebts];
				}

				// Fetch generatedDebtDues for the selected month
				const debtIds = debts.map(debt => (debt._id || debt).toString());
				// Create a map of debt ID to debt object for quick lookup
				const debtMap = {};
				debts.forEach(debt => {
					const debtId = (debt._id || debt).toString();
					debtMap[debtId] = debt;
				});

				let debtDues = [];
				if (debtIds.length > 0) {
					const duesParams = new URLSearchParams();
					duesParams.set('dateFrom', monthStart);
					duesParams.set('dateTo', monthEnd);
					duesParams.set('allData', 'true');
					if (!includePaidDues) duesParams.set('paid', 'no');
					const duesResponse = await GeneratedDebtService.search(duesParams.toString());
					const allDues = duesResponse.data?.allDebtsDues || duesResponse.data?.debtsDues || duesResponse.data || [];
					// Filter dues that belong to our debts and enrich with debt info
					debtDues = allDues
						.filter(due => {
							if (!due.debt) return false;
							const debtId = (due.debt._id || due.debt).toString();
							return debtIds.includes(debtId);
						})
						.map(due => {
							const debtId = (due.debt._id || due.debt).toString();
							const debt = debtMap[debtId];
							return {
								...due,
								debtConcept: debt?.concept || due.debt?.concept || 'N/A',
								debtCustomId: debt?.customId || due.debt?.customId || 'N/A',
								// Rides on the due so account roll-ups can gate each source.
								debtPaymentTo: resolveDebtPaymentTo(debt),
							};
						});
				}

				// Group debt dues by concept
				const debtDuesByConcept = {};
				// For account type, also group by asset
				const debtDuesByAsset = {};
				let assetMap = {}; // Map asset ID to asset object (for account type)
				
				debtDues.forEach(due => {
					const concept = due.debtConcept;
					if (!debtDuesByConcept[concept]) {
						debtDuesByConcept[concept] = [];
					}
					debtDuesByConcept[concept].push(due);
					
					// For account type, group by asset
					if (type === 'account') {
						// Get asset ID from debt (via contract or assetSale)
						let assetId = null;
						if (due.debt) {
							const debt = debtMap[(due.debt._id || due.debt).toString()];
							if (debt) {
								// Try to get asset from contract.property
								if (debt.contract?.property) {
									assetId = (debt.contract.property._id || debt.contract.property).toString();
									if (!assetMap[assetId]) {
										assetMap[assetId] = debt.contract.property;
									}
								} 
								// Try to get asset from assetSale.asset
								else if (debt.assetSale?.asset) {
									assetId = (debt.assetSale.asset._id || debt.assetSale.asset).toString();
									if (!assetMap[assetId]) {
										assetMap[assetId] = debt.assetSale.asset;
									}
								}
							}
						}
						
						// Only group if we found an asset ID
						if (assetId) {
							if (!debtDuesByAsset[assetId]) {
								debtDuesByAsset[assetId] = [];
							}
							debtDuesByAsset[assetId].push(due);
						}
					}
				});

				// Split dues: income vs expense concepts (treatAsExpense deduction)
				const gastoDues = debtDues.filter(due => expenseConcepts.has(due.debtConcept));

				// Compute per-asset gastoDuesByAsset (account type)
				const gastoDuesByAsset = {};
				if (type === 'account') {
					Object.keys(debtDuesByAsset).forEach(assetId => {
						gastoDuesByAsset[assetId] = (debtDuesByAsset[assetId] || []).filter(due => expenseConcepts.has(due.debtConcept));
					});
				}

				// Calculate debt dues total (income only — IRPF/commission base)
				const debtDuesTotal = debtDues
					.filter(due => !expenseConcepts.has(due.debtConcept))
					.reduce((sum, due) => {
						const amount = due.amount || 0;
						const surcharge = due.surcharge || 0;
						return sum + amount + surcharge;
					}, 0);
				// Expense concepts total (deduction, excluded from IRPF/commission base)
				const gastoActivoFijoTotal = gastoDues.reduce((sum, due) => sum + (due.amount || 0) + (due.surcharge || 0), 0);

				// Fetch gotasettings for IRPF
				const settingsResponse = await ConfigService.listPublic();
				const settings = settingsResponse.data || {};
				const irpf = settings.irpf || 0;

				// Rent the agency actually holds. debtDuesTotal stays the commission
				// base (commission is earned regardless of who collects); this is what
				// enters the saldo and what IRPF is withheld from.
				const collectedDuesTotal = debtDues
					.filter(due => !expenseConcepts.has(due.debtConcept) && dueIsCollected(due))
					.reduce((sum, due) => sum + (due.amount || 0) + (due.surcharge || 0), 0);

				// No collection, no withholding.
				const irpfDeduction = (collectedDuesTotal * irpf) / 100;

				// Recorded commissions always affect the balance. The feature flag only
				// controls whether the commission-management UI is available.
				let commissions = [];
				let commissionsByAsset = {};
				let commissionsTotal = 0;

				if (type === 'contract') {
					const commRes = await commissionService.list({ contract: id });
					commissions = commRes.data || [];
					commissions.forEach(c => {
						commissionsTotal += computeCommissionDeduction(c, debtDuesTotal, irpfDeduction);
					});
				} else if (type === 'assetSale') {
					const commRes = await commissionService.list({ assetSale: id });
					commissions = commRes.data || [];
					commissions.forEach(c => {
						commissionsTotal += computeCommissionDeduction(c, debtDuesTotal, irpfDeduction);
					});
				} else if (type === 'account' && assetIds && assetIds.length > 0) {
					// allContracts and allAssetSales were collected above during debt fetching
					// Fetch commissions for each contract and assetSale in parallel
					const contractCommPromises = allContracts.map(c =>
						commissionService.list({ contract: (c._id || c).toString() }).catch(() => ({ data: [] }))
					);
					const assetSaleCommPromises = allAssetSales.map(a =>
						commissionService.list({ assetSale: (a._id || a).toString() }).catch(() => ({ data: [] }))
					);
					const [contractCommResults, assetSaleCommResults] = await Promise.all([
						Promise.all(contractCommPromises),
						Promise.all(assetSaleCommPromises),
					]);
					const allCommissions = [
						...contractCommResults.flatMap(r => r.data || []),
						...assetSaleCommResults.flatMap(r => r.data || []),
					];
					commissions = allCommissions;

					// Group by asset using contract.property or assetSale.asset
					allCommissions.forEach(c => {
						let assetId = null;
						if (c.contract?.property) {
							assetId = (c.contract.property._id || c.contract.property).toString();
						} else if (c.assetSale?.asset) {
							assetId = (c.assetSale.asset._id || c.assetSale.asset).toString();
						}
						if (assetId) {
							if (!commissionsByAsset[assetId]) commissionsByAsset[assetId] = [];
							commissionsByAsset[assetId].push(c);
						}
					});

					// Compute commissionsTotal using per-asset dues
					Object.keys(commissionsByAsset).forEach(assetId => {
						const assetDues = debtDuesByAsset[assetId] || [];
						// Commission base is ALL rent — it's earned regardless of who collects.
						const assetIncomeDues = assetDues.filter(due => !expenseConcepts.has(due.debtConcept));
						const assetDuesTotal = assetIncomeDues.reduce((sum, due) => sum + (due.amount || 0) + (due.surcharge || 0), 0);
						// IRPF only on the part the agency actually holds.
						const assetCollectedTotal = assetIncomeDues
							.filter(dueIsCollected)
							.reduce((sum, due) => sum + (due.amount || 0) + (due.surcharge || 0), 0);
						const assetIrpfDed = (assetCollectedTotal * irpf) / 100;
						commissionsByAsset[assetId].forEach(c => {
							commissionsTotal += computeCommissionDeduction(c, assetDuesTotal, assetIrpfDed);
						});
					});
				}

				// Fetch bills for the asset(s) in the selected month
				const billsParams = new URLSearchParams();
				billsParams.set('balanceFrom', monthStart);
				billsParams.set('balanceTo', monthEnd);
				if (!includePaidBills) billsParams.set('paid', 'no');
				const billsResponse = await BillService.search(billsParams.toString());
				const allBills = billsResponse.data || [];
				
				let bills = [];
				let billsByAsset = {};
				
				if (type === 'account') {
					// For account, use the assetIds we already fetched, filter bills, and group by asset
					// assetIds was already fetched above
					if (assetIds && assetIds.length > 0) {
						// Fetch asset details to get names (only if not already populated from debt dues)
						const missingAssetIds = assetIds.filter(assetId => !assetMap[assetId.toString()]);
						if (missingAssetIds.length > 0) {
							const assetDetailsPromises = missingAssetIds.map(assetId => 
								AssetService.getDetails(assetId).catch(err => {
									console.error(`Error fetching asset ${assetId}:`, err);
									return { data: { _id: assetId, customId: assetId } };
								})
							);
							const assetDetailsResults = await Promise.all(assetDetailsPromises);
							assetDetailsResults.forEach(result => {
								const asset = result.data;
								const assetId = (asset._id || asset).toString();
								if (!assetMap[assetId]) {
									assetMap[assetId] = asset;
								}
							});
						}
						
						bills = allBills.filter(bill => {
							const billAssetId = (bill.asset?._id || bill.asset)?.toString();
							return billAssetId && assetIds.includes(billAssetId);
						});
						
						// Group bills by asset
						bills.forEach(bill => {
							const billAssetId = (bill.asset?._id || bill.asset)?.toString();
							if (!billsByAsset[billAssetId]) {
								billsByAsset[billAssetId] = [];
							}
							billsByAsset[billAssetId].push(bill);
						});
					}
				} else {
					// For other types, filter bills that belong to our asset
					const assetId = getAssetId();
					if (assetId) {
						bills = allBills.filter(bill => 
							(bill.asset?._id || bill.asset)?.toString() === assetId.toString()
						);
					}
				}

				// Owner/legacy bills reduce the settlement. Tenant bills are collected
				// by the agency and increase the amount to liquidate.
				const billsTotal = bills
					.filter(billChargesOwner)
					.reduce((sum, bill) => sum + (bill.amount || 0), 0);
				const tenantBillsTotal = bills
					.filter(billChargesTenant)
					.reduce((sum, bill) => sum + (bill.amount || 0), 0);

				// Calculate grand total
				// Signed saldo: the sign carries the direction of payment. Positive means
				// the agency owes the propietario; negative means the propietario owes
				// the agency (rent collected directly, or costs exceeding the rent).
				const grandTotal = collectedDuesTotal - irpfDeduction - commissionsTotal - gastoActivoFijoTotal - billsTotal + tenantBillsTotal;

				// For account, use the currency from the first debt or default to USD
				let currency = 'USD';
				if (type === 'account' && debts.length > 0) {
					currency = debts[0].currency || 'USD';
				} else {
					currency = data.currency || 'USD';
				}

				// For account type, ensure all assets with bills are included even if no debt dues
				if (type === 'account' && assetIds && assetIds.length > 0) {
					// Add assets that have bills but no debt dues
					Object.keys(billsByAsset).forEach(billAssetId => {
						if (!debtDuesByAsset[billAssetId]) {
							debtDuesByAsset[billAssetId] = [];
						}
					});
				}

				// Per-currency totals — never converted, only segregated (see
				// balanceTotals.js). For account type, roll the per-asset buckets
				// up into the whole-balance totals, folding in any dues/bills that
				// couldn't be traced to an asset (same pre-existing gap as the
				// legacy top-level fields above) with no commission attributed.
				let assetTotalsByCurrency = {};
				let totals;
				if (type === 'account') {
					assetTotalsByCurrency = bucketTotalsByAsset({ debtDuesByAsset, billsByAsset, commissionsByAsset, expenseConcepts, irpf });
					const assignedDueIds = new Set(Object.values(debtDuesByAsset).flat().map(due => String(due._id)));
					const assignedBillIds = new Set(Object.values(billsByAsset).flat().map(bill => String(bill._id)));
					const unassignedBucket = bucketTotals({
						dues: debtDues.filter(due => !assignedDueIds.has(String(due._id))),
						bills: bills.filter(bill => !assignedBillIds.has(String(bill._id))),
						commissions: [],
						expenseConcepts,
						irpf,
					});
					totals = mergeBucketResults([...Object.values(assetTotalsByCurrency), unassignedBucket]);
				} else {
					totals = bucketTotals({ dues: debtDues, bills, commissions, expenseConcepts, irpf });
				}

				setBalanceData({
					debtDuesTotal,
					collectedDuesTotal,
					irpfDeduction,
					commissionsTotal,
					gastoActivoFijoTotal,
					billsTotal,
					tenantBillsTotal,
					grandTotal,
					currency,
					debtDues,
					debtDuesByConcept,
					bills,
					commissions,
					irpf,
					gastoDues,
					byCurrency: totals.byCurrency,
					currencies: totals.currencies,
					mixedCurrency: totals.mixedCurrency,
					assetTotalsByCurrency,
					includePaidBills,
					includePaidDues,
					// For account type, include asset-grouped data
					...(type === 'account' ? {
						debtDuesByAsset,
						billsByAsset,
						assetMap,
						assetIds,
						commissionsByAsset,
						gastoDuesByAsset,
					} : {}),
				});
			} catch (err) {
				setError(getError(err));
			} finally {
				setLoading(false);
			}
		};

		fetchBalanceData();
		// expenseConcepts is read inside fetchBalanceData for the income/expense
		// split; when the concept fetch resolves the balance is recomputed.
	}, [selectedMonth, data, id, type, expenseConcepts, includePaidBills, includePaidDues]);

	const handleMonthChange = (e) => {
		setSelectedMonth(e.target.value);
	};

	const formatCurrency = (value, currency) => {
		const resolvedCurrency = currency || balanceData?.currency || 'USD';
		const currencySymbol = showCurrency(resolvedCurrency) || 'U$S';
		return currencySymbol + ' ' + value.toLocaleString('es-UY', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		});
	};

	const getConceptLabel = (concept) => {
		const conceptMap = {
			// Display names of fetched expense concepts; the legacy map below
			// wins on overlap so historical labels render unchanged.
			...expenseConceptNames,
			'alquileres': 'Alquileres',
			'ventas-a-plazo': 'Ventas a Plazo',
			'prestamos-amortizable': 'Préstamos Amortizables',
			'prestamos-interés': 'Préstamos con Interés',
			'gasto-de-activo-fijo': 'Gasto de Activo Fijo',
			'garantia-de-alquiler': 'Garantía de Alquiler',
		};
		return conceptMap[concept] || concept;
	};

	const exportToPDF = async () => {
		if (!balanceData || !data || !selectedMonth) {
			toast.error('Cargando información...');
			return;
		}

		setPdfLoading(true);
		try {
			const response = await AccountService.getBalancePDF(data._id, selectedMonth, type, { includePaidBills, includePaidDues });
			
			// Create blob and download
			const blob = new Blob([response.data], { type: 'application/pdf' });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			const fileName = `Balance_${type === 'account' ? data.name : data.customId || data._id}_${moment.utc(selectedMonth).format('MM-YYYY')}_${moment.utc().format('DD-MM-YYYY')}.pdf`;
			link.download = fileName;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error('Error downloading PDF:', error);
			toast.error(getError(error));
		} finally {
			setPdfLoading(false);
		}
	};

	// Fetch client phone when entity changes
	useEffect(() => {
		if (!id || !type) return;
		AccountService.getBalanceClientPhone(id, type)
			.then(res => { defaultPhoneRef.current = res.data?.clientPhone || ''; })
			.catch(() => { defaultPhoneRef.current = ''; });
	}, [id, type]);

	const handleEmail = () => {
		setEmailInput('');
		setShowEmailModal(true);
	};

	const handleCloseEmailModal = () => {
		if (emailSending) return;
		setShowEmailModal(false);
	};

	const handleSendEmail = async () => {
		const recipientEmail = emailInput.trim();
		if (!recipientEmail || !isValidEmail(recipientEmail)) {
			toast.error('Ingrese un email');
			return;
		}
		setEmailSending(true);
		try {
			await AccountService.sendBalanceEmail(data._id, selectedMonth, type, recipientEmail, { includePaidBills, includePaidDues });
			toast.success('Email enviado correctamente');
			setShowEmailModal(false);
		} catch (err) {
			toast.error(getError(err));
		} finally {
			setEmailSending(false);
		}
	};

	const handleWhatsApp = () => {
		setPhoneInput(defaultPhoneRef.current || '');
		setShowWhatsAppModal(true);
	};

	const handleCloseWhatsAppModal = () => {
		if (sharing) return;
		setShowWhatsAppModal(false);
	};

	const handleSendWhatsApp = async () => {
		if (!isValidPhone(phoneInput)) {
			toast.error('Ingrese un número de WhatsApp válido con código de país (ej: 59891234567)');
			return;
		}
		try {
			setSharing(true);
			const res = await AccountService.createBalanceShareLink(data._id, selectedMonth, type, { includePaidBills, includePaidDues });
			const { url } = res.data;
			const digits = normalizePhone(phoneInput);
			const text = encodeURIComponent(`Hola, te comparto el balance para descargar: ${url}`);
			window.open(`https://wa.me/${digits}?text=${text}`, '_blank', 'noopener,noreferrer');
			toast.success('Enlace generado');
			setShowWhatsAppModal(false);
		} catch (err) {
			toast.error(getError(err));
		} finally {
			setSharing(false);
		}
	};

	// Load the month's facturación record (latest attempt) whenever the month changes.
	useEffect(() => {
		if (!facturacionAvailable || !data?._id || !selectedMonth) {
			setFacturacionRecord(null);
			return;
		}
		facturacionService.getBalanceFacturacion(type, data._id, selectedMonth)
			.then((res) => setFacturacionRecord(res.data?.facturacion || null))
			.catch((err) => {
				setFacturacionRecord(null);
				// 404 = feature off/unconfigured server-side: hide the whole surface.
				if (err?.response?.status === 404) setFacturacionAvailable(false);
			});
	}, [facturacionAvailable, type, data?._id, selectedMonth]);

	const handleOpenFacturar = async () => {
		setShowFacturarModal(true);
		setFacturarPreview(null);
		setFacturarPreviewError('');
		setFacturarPreviewLoading(true);
		try {
			const res = await facturacionService.previewFacturacion(type, data._id, selectedMonth);
			setFacturarPreview(res.data);
		} catch (err) {
			setFacturarPreviewError(getError(err));
		} finally {
			setFacturarPreviewLoading(false);
		}
	};

	const handleConfirmFacturar = async () => {
		setFacturarSending(true);
		try {
			const res = await facturacionService.facturar(type, data._id, selectedMonth);
			setFacturacionRecord(res.data?.facturacion || null);
			setShowFacturarModal(false);
			toast.success('Borrador creado en GotaBilling');
		} catch (err) {
			// 409: someone else already invoiced this month — adopt their record.
			if (err?.response?.status === 409 && err.response.data?.facturacion) {
				setFacturacionRecord(err.response.data.facturacion);
				setShowFacturarModal(false);
			}
			toast.error(getError(err));
		} finally {
			setFacturarSending(false);
		}
	};

	const handleFacturacionRefresh = async () => {
		if (!facturacionRecord?._id) return;
		setFacturacionRefreshing(true);
		try {
			const res = await facturacionService.refreshComprobante(facturacionRecord._id);
			setFacturacionRecord(res.data?.facturacion || facturacionRecord);
			toast.success('Estado actualizado');
		} catch (err) {
			toast.error(getError(err));
		} finally {
			setFacturacionRefreshing(false);
		}
	};

	const handleFacturacionPdf = async () => {
		if (!facturacionRecord?._id) return;
		try {
			const response = await facturacionService.downloadPdf(facturacionRecord._id);
			const blob = new Blob([response.data], { type: 'application/pdf' });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = `CFE_${facturacionRecord.serie || 'SN'}-${facturacionRecord.numero || 0}_${selectedMonth}.pdf`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (err) {
			toast.error(getError(err));
		}
	};

	return (
		<>
			<Modal
				show={showWhatsAppModal}
				onHide={handleCloseWhatsAppModal}
				onExited={() => setPhoneInput('')}
				centered
				animation={false}
				backdrop={sharing ? 'static' : true}
				keyboard={!sharing}
				dialogClassName='tw:max-w-[520px]'
				contentClassName='entity-list-page el-modal tw:overflow-hidden tw:rounded-[14px] tw:border-0 tw:font-sans tw:text-[14px]/[1.45] tw:text-ink-0 tw:shadow-overlay'
			>
				<Modal.Header
					closeButton={!sharing}
					className='tw:items-start tw:border-b tw:border-border tw:px-[22px] tw:pt-[20px] tw:pb-[14px]'
				>
					<div className='tw:flex tw:items-start tw:gap-3'>
						<span className='tw:mt-0.5 tw:flex tw:h-[38px] tw:w-[38px] tw:shrink-0 tw:items-center tw:justify-center tw:rounded-[9px] tw:bg-ok-soft tw:text-ok'>
							<FaWhatsapp />
						</span>
						<div>
							<Modal.Title className='tw:text-[17px]/[1.3] tw:font-bold tw:tracking-[-0.01em] tw:text-ink-0'>
								Enviar balance por WhatsApp
							</Modal.Title>
							<p className='tw:mb-0 tw:mt-1 tw:text-[12.5px] tw:text-ink-2'>
								Genera un enlace directo para compartir el balance con un número válido.
							</p>
						</div>
					</div>
				</Modal.Header>
				<Modal.Body className='tw:flex tw:flex-col tw:gap-4 tw:px-[22px] tw:py-[18px]'>
					<Form.Group>
						<Form.Label className='tw:mb-1 tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.08em] tw:text-ink-3'>
							Número de WhatsApp
						</Form.Label>
						<Form.Control
							type='tel'
							placeholder='59891234567'
							value={phoneInput}
							onChange={(e) => setPhoneInput(e.target.value)}
							onKeyDown={(e) => e.key === 'Enter' && handleSendWhatsApp()}
							isInvalid={!!phoneInput && !isValidPhone(phoneInput)}
							disabled={sharing}
							className='tw:rounded-[8px] tw:border-border tw:bg-surface tw:px-3 tw:py-2.5 tw:text-[13.5px] tw:text-ink-0 tw:shadow-none tw:placeholder:text-ink-4 tw:focus:border-accent tw:focus:shadow-none tw:focus:ring-0'
						/>
						<Form.Text className='tw:mt-1.5 tw:block tw:text-[12px] tw:text-ink-3'>
							Incluir código de país. Ej: 59891234567 (Uruguay: 598 + 8 dígitos)
						</Form.Text>
						<Form.Control.Feedback type='invalid'>
							Número inválido. Ej: 59891234567
						</Form.Control.Feedback>
					</Form.Group>
				</Modal.Body>
				<Modal.Footer className='tw:justify-end tw:gap-2 tw:border-t tw:border-border tw:bg-surface-2 tw:px-[22px] tw:py-[14px]'>
					<Button variant='default' icon={<FaTimes />} onClick={handleCloseWhatsAppModal} disabled={sharing}>
						Cancelar
					</Button>
					<Button
						variant='primary'
						icon={sharing ? <Spinner animation='border' size='sm' /> : <FaPaperPlane />}
						onClick={handleSendWhatsApp}
						disabled={sharing || !phoneInput || !isValidPhone(phoneInput)}
						className='tw:min-w-[148px]'
					>
						{sharing ? 'Generando enlace...' : 'Enviar'}
					</Button>
				</Modal.Footer>
			</Modal>
			<Modal
				show={showEmailModal}
				onHide={handleCloseEmailModal}
				onExited={() => setEmailInput('')}
				centered
				animation={false}
				backdrop={emailSending ? 'static' : true}
				keyboard={!emailSending}
				dialogClassName='tw:max-w-[520px]'
				contentClassName='entity-list-page el-modal tw:overflow-hidden tw:rounded-[14px] tw:border-0 tw:font-sans tw:text-[14px]/[1.45] tw:text-ink-0 tw:shadow-overlay'
			>
				<Modal.Header
					closeButton={!emailSending}
					className='tw:items-start tw:border-b tw:border-border tw:px-[22px] tw:pt-[20px] tw:pb-[14px]'
				>
					<div className='tw:flex tw:items-start tw:gap-3'>
						<span className='tw:mt-0.5 tw:flex tw:h-[38px] tw:w-[38px] tw:shrink-0 tw:items-center tw:justify-center tw:rounded-[9px] tw:bg-accent-soft tw:text-accent'>
							<FaEnvelope />
						</span>
						<div>
							<Modal.Title className='tw:text-[17px]/[1.3] tw:font-bold tw:tracking-[-0.01em] tw:text-ink-0'>
								Enviar balance por email
							</Modal.Title>
							<p className='tw:mb-0 tw:mt-1 tw:text-[12.5px] tw:text-ink-2'>
								Comparte el balance adjunto con la dirección de correo que indiques.
							</p>
						</div>
					</div>
				</Modal.Header>
				<Modal.Body className='tw:flex tw:flex-col tw:gap-4 tw:px-[22px] tw:py-[18px]'>
					<Form.Group>
						<Form.Label className='tw:mb-1 tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.08em] tw:text-ink-3'>
							Email
						</Form.Label>
						<Form.Control
							type='email'
							placeholder='ejemplo@correo.com'
							value={emailInput}
							onChange={(e) => setEmailInput(e.target.value)}
							onKeyDown={(e) => e.key === 'Enter' && handleSendEmail()}
							isInvalid={!!emailInput && !isValidEmail(emailInput)}
							disabled={emailSending}
							className='tw:rounded-[8px] tw:border-border tw:bg-surface tw:px-3 tw:py-2.5 tw:text-[13.5px] tw:text-ink-0 tw:shadow-none tw:placeholder:text-ink-4 tw:focus:border-accent tw:focus:shadow-none tw:focus:ring-0'
						/>
						<Form.Text className='tw:mt-1.5 tw:block tw:text-[12px] tw:text-ink-3'>
							Se enviará el balance en PDF como adjunto.
						</Form.Text>
						<Form.Control.Feedback type='invalid'>
							Ingrese un email válido
						</Form.Control.Feedback>
					</Form.Group>
				</Modal.Body>
				<Modal.Footer className='tw:justify-end tw:gap-2 tw:border-t tw:border-border tw:bg-surface-2 tw:px-[22px] tw:py-[14px]'>
					<Button variant='default' icon={<FaTimes />} onClick={handleCloseEmailModal} disabled={emailSending}>
						Cancelar
					</Button>
					<Button
						variant='primary'
						icon={emailSending ? <Spinner animation='border' size='sm' /> : <FaPaperPlane />}
						onClick={handleSendEmail}
						disabled={emailSending || !emailInput || !isValidEmail(emailInput)}
						className='tw:min-w-[124px]'
					>
						{emailSending ? 'Enviando...' : 'Enviar'}
					</Button>
				</Modal.Footer>
			</Modal>
			<Modal
				show={showFacturarModal}
				onHide={() => !facturarSending && setShowFacturarModal(false)}
				centered
				animation={false}
				backdrop={facturarSending ? 'static' : true}
				keyboard={!facturarSending}
				dialogClassName='tw:max-w-[720px]'
				contentClassName='entity-list-page el-modal tw:overflow-hidden tw:rounded-[14px] tw:border-0 tw:font-sans tw:text-[14px]/[1.45] tw:text-ink-0 tw:shadow-overlay'
			>
				<Modal.Header
					closeButton={!facturarSending}
					className='tw:items-start tw:border-b tw:border-border tw:px-[22px] tw:pt-[20px] tw:pb-[14px]'
				>
					<div className='tw:flex tw:items-start tw:gap-3'>
						<span className='tw:mt-0.5 tw:flex tw:h-[38px] tw:w-[38px] tw:shrink-0 tw:items-center tw:justify-center tw:rounded-[9px] tw:bg-accent-soft tw:text-accent'>
							<FaFileInvoiceDollar />
						</span>
						<div>
							<Modal.Title className='tw:text-[17px]/[1.3] tw:font-bold tw:tracking-[-0.01em] tw:text-ink-0'>
								Facturar balance
							</Modal.Title>
							<p className='tw:mb-0 tw:mt-1 tw:text-[12.5px] tw:text-ink-2'>
								Se creará un borrador de comprobante en GotaBilling con los ingresos del mes.
								La emisión (folio y CAE) se confirma después desde GotaBilling.
							</p>
						</div>
					</div>
				</Modal.Header>
				<Modal.Body className='tw:flex tw:flex-col tw:gap-4 tw:px-[22px] tw:py-[18px]'>
					{facturarPreviewLoading ? (
						<div className='tw:flex tw:justify-center tw:py-4'><Spinner animation='border' /></div>
					) : facturarPreviewError ? (
						<div className='tw:rounded-[7px] tw:border tw:border-danger tw:bg-danger-soft tw:px-4 tw:py-3 tw:text-[13px]'>
							{facturarPreviewError}
						</div>
					) : facturarPreview ? (
						<>
							<div className='tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:text-[13.5px] tw:text-ink-1'>
								<span className='tw:font-semibold tw:text-ink-0'>Receptor:</span>
								<span>{facturarPreview.receptor?.razonSocial}</span>
								{facturarPreview.receptor?.documento && (
									<span className='tw:text-ink-3'>
										· {facturarPreview.receptor.tipoDocumento} {facturarPreview.receptor.documento}
									</span>
								)}
								<span className='tw:rounded-full tw:bg-accent-soft tw:px-2.5 tw:py-0.5 tw:text-[11.5px] tw:font-semibold tw:text-accent'>
									{facturarPreview.tipoCfe === 111 ? 'e-Factura (111)' : 'e-Ticket (101)'}
								</span>
							</div>
							<div className='tw:overflow-hidden tw:rounded-panel tw:border tw:border-border'>
								<div className='tw:overflow-x-auto'>
									<table className='tw:w-full tw:border-collapse tw:tabular-nums'>
										<thead>
											<tr>
												<th className='tw:px-4 tw:py-2.5 tw:text-left tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.06em] tw:text-ink-3 tw:bg-surface-2 tw:border-b tw:border-border'>Descripción</th>
												<th className='tw:px-4 tw:py-2.5 tw:text-right tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.06em] tw:text-ink-3 tw:bg-surface-2 tw:border-b tw:border-border tw:whitespace-nowrap' style={{ width: '15%' }}>Cantidad</th>
												<th className='tw:px-4 tw:py-2.5 tw:text-right tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.06em] tw:text-ink-3 tw:bg-surface-2 tw:border-b tw:border-border tw:whitespace-nowrap' style={{ width: '25%' }}>Precio unitario</th>
											</tr>
										</thead>
										<tbody>
											{facturarPreview.payload?.items?.map((item, idx) => (
												<tr key={idx} className='tw:border-b tw:border-border'>
													<td className='tw:px-4 tw:py-2.5 tw:text-[13.5px] tw:text-ink-1'>{item.descripcion}</td>
													<td className='tw:px-4 tw:py-2.5 tw:text-right tw:text-[13.5px] tw:text-ink-1'>{item.cantidad}</td>
													<td className='tw:px-4 tw:py-2.5 tw:text-right tw:text-[13.5px] tw:text-ink-1'>
														{item.precioUnitario.toLocaleString('es-UY', { minimumFractionDigits: 2 })}
													</td>
												</tr>
											))}
										</tbody>
										<tfoot>
											<tr className='tw:bg-surface-2'>
												<th colSpan={2} className='tw:px-4 tw:py-2.5 tw:text-left tw:text-[13px] tw:font-semibold tw:text-ink-0'>
													Total ({facturarPreview.payload?.moneda})
												</th>
												<th className='tw:px-4 tw:py-2.5 tw:text-right tw:text-[13px] tw:font-semibold tw:text-ink-0'>
													{facturarPreview.lineasTotal?.toLocaleString('es-UY', { minimumFractionDigits: 2 })}
												</th>
											</tr>
										</tfoot>
									</table>
								</div>
							</div>
							{facturarPreview.warnings?.map((warning, idx) => (
								<div
									key={idx}
									className='tw:rounded-[7px] tw:border tw:border-warn tw:bg-warn-soft tw:px-4 tw:py-3 tw:text-[13px]'
								>
									{warning}
								</div>
							))}
						</>
					) : null}
				</Modal.Body>
				<Modal.Footer className='tw:justify-end tw:gap-2 tw:border-t tw:border-border tw:bg-surface-2 tw:px-[22px] tw:py-[14px]'>
					<Button variant='default' icon={<FaTimes />} onClick={() => setShowFacturarModal(false)} disabled={facturarSending}>
						Cancelar
					</Button>
					<Button
						variant='primary'
						icon={facturarSending ? <Spinner animation='border' size='sm' /> : <FaFileInvoiceDollar />}
						onClick={handleConfirmFacturar}
						disabled={facturarSending || facturarPreviewLoading || !facturarPreview || !!facturarPreviewError}
						className='tw:min-w-[168px]'
					>
						{facturarSending ? 'Creando borrador...' : 'Crear borrador'}
					</Button>
				</Modal.Footer>
			</Modal>
			<div className='tw:flex tw:flex-col tw:gap-4' style={{ position: 'relative' }}>
				<BusyOverlay active={pdfLoading} label='Generando PDF...' />

				{!loading && !error && balanceData && type === 'account' && (() => {
					const currencies = balanceData.currencies?.length ? balanceData.currencies : [balanceData.currency];
					const multiCurrency = currencies.length > 1;
					const stats = currencies.flatMap((currency) => {
						const bucket = balanceData.byCurrency?.[currency] || {};
						const tag = multiCurrency ? ` (${currency})` : '';
						const gastos = (bucket.gastoActivoFijoTotal || 0) + (bucket.billsTotal || 0);
						return [
							{
								label: `Ingresos brutos${tag}`,
								value: formatCurrency(bucket.debtDuesTotal || 0, currency),
								mono: true,
								// When part of the rent is collected directly by the propietario it
								// is still billed (and still the commission base) but never enters the
								// saldo — say so rather than leaving the two figures unexplained.
								sub: (bucket.collectedDuesTotal ?? bucket.debtDuesTotal) !== bucket.debtDuesTotal
									? `${formatCurrency(bucket.collectedDuesTotal || 0, currency)} cobrados por la inmobiliaria`
									: `${Object.keys(balanceData.debtDuesByAsset || {}).filter((assetId) => (balanceData.debtDuesByAsset[assetId] || []).some((due) => !expenseConcepts.has(due.debtConcept) && (resolveDueCurrency(due) || currency) === currency)).length} activos con cuotas`,
								accent: '--tw-color-ok',
							},
							{
								label: `Retenciones${tag}`,
								value: `−${formatCurrency((bucket.irpfDeduction || 0) + (bucket.commissionsTotal || 0), currency)}`,
								mono: true,
								sub: `IRPF ${balanceData.irpf}%`,
								accent: '--tw-color-danger',
							},
							{
								label: `Gastos propietario${tag}`,
								value: `−${formatCurrency(gastos, currency)}`,
								mono: true,
								sub: 'Activos fijos y otros',
								accent: gastos > 0 ? '--tw-color-danger' : undefined,
							},
							{
								label: `Cargos al inquilino${tag}`,
								value: `+${formatCurrency(bucket.tenantBillsTotal || 0, currency)}`,
								mono: true,
								sub: 'Cobrado por inmobiliaria',
								accent: (bucket.tenantBillsTotal || 0) > 0 ? '--tw-color-ok' : undefined,
							},
							{
								label: `Total del mes${tag}`,
								value: formatCurrency(Math.abs(bucket.grandTotal || 0), currency),
								mono: true,
								sub: (bucket.grandTotal || 0) >= 0 ? 'Neto a liquidar al propietario' : 'A cobrar al propietario',
								accent: (bucket.grandTotal || 0) >= 0 ? undefined : '--tw-color-danger',
							},
						];
					});
					return <StatStrip stats={stats} />;
				})()}

				{facturacionAvailable && facturacionRecord && (
					<FacturacionBalancePanel
						record={facturacionRecord}
						refreshing={facturacionRefreshing}
						onRefresh={handleFacturacionRefresh}
						onFacturarNuevamente={handleOpenFacturar}
						onDownloadPdf={handleFacturacionPdf}
					/>
				)}

				<div className='tw:overflow-hidden tw:rounded-panel tw:border tw:border-border tw:bg-surface tw:shadow-sm'>
					<div className='tw:flex tw:flex-wrap tw:items-end tw:gap-4 tw:border-b tw:border-border tw:px-4 tw:py-3.5'>
						<div className='tw:w-[220px]'>
							<label className='tw:mb-1 tw:block tw:text-[12px] tw:font-semibold tw:text-ink-2'>Mes</label>
							<Select
								id='balance-month'
								options={monthOptions}
								value={selectedMonth}
								onChange={handleMonthChange}
							/>
						</div>
						<Checkbox
							id='balance-include-paid-bills'
							label='Incluir gastos pagos'
							checked={includePaidBills}
							onChange={(e) => setIncludePaidBills(e.target.checked)}
						/>
						<Checkbox
							id='balance-include-paid-dues'
							label='Incluir cuotas pagas'
							checked={includePaidDues}
							onChange={(e) => setIncludePaidDues(e.target.checked)}
						/>
						<div className='tw:flex-1' />
						<Button size='sm' icon={<FaFilePdf />} onClick={exportToPDF} disabled={!balanceData || loading || pdfLoading}>
							Descargar PDF
						</Button>
						<Button size='sm' icon={<FaWhatsapp />} onClick={handleWhatsApp} disabled={!balanceData || loading || pdfLoading}>
							Enviar por WhatsApp
						</Button>
						<Button size='sm' icon={<FaEnvelope />} onClick={handleEmail} disabled={!balanceData || loading || pdfLoading}>
							Enviar por Email
						</Button>
						{facturacionAvailable && (
							<Button
								size='sm'
								icon={<FaFileInvoiceDollar />}
								onClick={handleOpenFacturar}
								disabled={!balanceData || loading || pdfLoading || (facturacionRecord && facturacionRecord.estado !== 'anulado')}
							>
								Facturar
							</Button>
						)}
					</div>

					<div>
						{loading ? (
							<BalanceTableSkeleton type={type} />
						) : error ? (
							<div className='tw:p-5'>
								<div className='tw:rounded-[7px] tw:border tw:border-danger tw:bg-danger-soft tw:px-4 tw:py-3 tw:text-[13px]'>
									{error}
								</div>
							</div>
						) : balanceData ? (
							<>
								{balanceCaption && (
									<div className='tw:mx-5 tw:mt-3 tw:mb-3 tw:rounded-[7px] tw:border-l-2 tw:border-accent tw:bg-surface-2 tw:px-4 tw:py-2.5 tw:text-[13px] tw:font-semibold tw:text-ink-1'>
										{balanceCaption}
									</div>
								)}
								{type === 'account' ? (
								// Account type: one <tbody> per rental asset — no rowSpan arithmetic to
								// keep in sync, each group is self-contained.
									<div className='tw:overflow-x-auto'>
										<table className='tw:w-full tw:border-collapse tw:tabular-nums'>
											<thead>
												<tr>
													<th className='tw:px-4 tw:py-3 tw:text-left tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.06em] tw:text-ink-3 tw:bg-surface-2 tw:border-b tw:border-border tw:whitespace-nowrap' style={{ width: '65%' }}>Concepto</th>
													<th className='tw:px-4 tw:py-3 tw:text-right tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.06em] tw:text-ink-3 tw:bg-surface-2 tw:border-b tw:border-border tw:whitespace-nowrap' style={{ width: '35%' }}>Monto</th>
												</tr>
											</thead>
											{(() => {
												// Get all unique asset IDs from both debtDuesByAsset and billsByAsset
												const allAssetIds = new Set([
													...(balanceData.debtDuesByAsset ? Object.keys(balanceData.debtDuesByAsset) : []),
													...(balanceData.billsByAsset ? Object.keys(balanceData.billsByAsset) : []),
												]);

												if (allAssetIds.size === 0) {
													return (
														<tbody>
															<tr>
																<td colSpan="2" className='tw:px-4 tw:py-6 tw:text-center tw:text-[13.5px] tw:text-ink-3'>
																No hay información para este mes
																</td>
															</tr>
														</tbody>
													);
												}

												return Array.from(allAssetIds).map(assetId => {
													const asset = balanceData.assetMap?.[assetId] || { customId: assetId };
													const assetDues = balanceData.debtDuesByAsset?.[assetId] || [];
													const assetBills = balanceData.billsByAsset?.[assetId] || [];

													// Group dues by concept for this asset
													const duesByConcept = {};
													assetDues.forEach(due => {
														const concept = due.debtConcept;
														if (!duesByConcept[concept]) {
															duesByConcept[concept] = [];
														}
														duesByConcept[concept].push(due);
													});

													// Per-currency totals come from the shared bucketing
													// (balanceTotals.js) — never recomputed here as a single
													// cross-currency number.
													const assetGastoDues = (balanceData.gastoDuesByAsset?.[assetId] || []);
													const assetIncomeDues = assetDues.filter(due => !expenseConcepts.has(due.debtConcept));
													const assetCommissionsRaw = (balanceData.commissionsByAsset?.[assetId] || []);
													// Currencies that actually have income dues — a currency present only
													// via bills (e.g. a UYU-only maintenance bill on a USD contract)
													// shouldn't get a zero-value "Total cuotas de deudas" row.
													const incomeDuesCurrencies = new Set(assetIncomeDues.map(due => resolveDueCurrency(due) || balanceData.currency));
													const assetTotals = balanceData.assetTotalsByCurrency?.[assetId] || { byCurrency: {}, currencies: [] };
													const totalsCurrencies = assetTotals.currencies.length > 0 ? assetTotals.currencies : [balanceData.currency];
													const multiCurrency = totalsCurrencies.length > 1;
													// Currencies with rent, or with a fixed commission deducted in them.
													const rentSectionCurrencies = totalsCurrencies.filter(currency => incomeDuesCurrencies.has(currency) || (assetTotals.byCurrency[currency]?.commissionsTotal || 0) > 0);
													const assetLabel = asset.customId || asset.name || assetId;

													return (
														<tbody key={assetId}>
															{/* Asset band */}
															<tr className='tw:bg-surface-3 tw:border-t tw:border-border-strong'>
																<td colSpan="2" className='tw:px-4 tw:py-[10px]'>
																	<div className='tw:flex tw:items-center tw:gap-[9px]'>
																		<FiHome className='tw:text-ink-2' style={{ fontSize: 15 }} />
																		<span className='tw:font-semibold tw:text-[13.5px] tw:text-ink-0' style={{ letterSpacing: '-0.005em' }}>{asset.name || assetLabel}</span>
																		<IdChip>{assetLabel}</IdChip>
																	</div>
																</td>
															</tr>
															{/* Section header */}
															<tr>
																<td colSpan="2" className='tw:px-4 tw:pt-[9px] tw:pb-[7px] tw:text-[10.5px] tw:font-bold tw:uppercase tw:tracking-[0.07em] tw:text-ink-3 tw:bg-surface-2'>
																Cuotas de deudas
																</td>
															</tr>
															{Object.keys(duesByConcept).filter(concept => !expenseConcepts.has(concept)).map(concept => {
																const dues = duesByConcept[concept];
																const conceptTotals = {};
																dues.forEach(due => {
																	const currency = resolveDueCurrency(due) || balanceData.currency;
																	conceptTotals[currency] = (conceptTotals[currency] || 0) + (due.amount || 0) + (due.surcharge || 0);
																});
																const conceptMulti = Object.keys(conceptTotals).length > 1;

																return (
																	<React.Fragment key={concept}>
																		{dues.map((due, index) => (
																			<tr key={due._id || index}>
																				<td className='tw:pr-4 tw:py-3 tw:pl-[30px] tw:text-[13.5px] tw:text-ink-0'>
																					{index === 0 && (
																						<><strong>{getConceptLabel(concept)}</strong><br/></>
																					)}
																					<span className={index === 0 ? '' : 'tw:ml-5'} style={{ display: 'inline-block' }}>
																						<span className='tw:font-mono tw:text-[11.5px] tw:text-ink-3'>{due.customId || 'N/A'}</span>
																						{!dueIsCollected(due) && (
																							<span className='tw:ml-2'><Pill tone='neutral'>Cobra propietario</Pill></span>
																						)}
																					</span>
																				</td>
																				<td className={classnames('tw:px-4 tw:py-3 tw:font-mono tw:text-[13px] tw:tabular-nums tw:whitespace-nowrap tw:text-right', dueIsCollected(due) ? 'tw:text-ok' : 'tw:text-ink-4')}>
																				+{formatCurrency((due.amount || 0) + (due.surcharge || 0), resolveDueCurrency(due))}
																				</td>
																			</tr>
																		))}
																		{Object.entries(conceptTotals).map(([currency, total]) => (
																			<tr key={`${concept}-${currency}`}>
																				<td className='tw:pr-4 tw:py-3 tw:pl-[30px] tw:text-[13px] tw:text-ink-2'>Subtotal {getConceptLabel(concept)}{conceptMulti ? ` (${currency})` : ''}</td>
																				<td className='tw:px-4 tw:py-3 tw:font-mono tw:text-[13px] tw:tabular-nums tw:whitespace-nowrap tw:text-right tw:text-ok'>
																				+{formatCurrency(total, currency)}
																				</td>
																			</tr>
																		))}
																	</React.Fragment>
																);
															})}
															{assetIncomeDues.length === 0 && (
																<tr>
																	<td colSpan="2" className='tw:pr-4 tw:py-3 tw:pl-[30px] tw:text-center tw:text-[13.5px] tw:text-ink-3'>
																	No hay cuotas de deudas
																	</td>
																</tr>
															)}
															{rentSectionCurrencies.map((currency) => {
																const bucket = assetTotals.byCurrency[currency];
																if (!bucket) return null;
																const tag = multiCurrency ? ` (${currency})` : '';
																return (
																	<React.Fragment key={currency}>
																		<tr>
																			<td className='tw:px-4 tw:py-3 tw:text-[13.5px] tw:font-semibold tw:text-ink-0'>Total cuotas de deudas{tag}</td>
																			<td className='tw:px-4 tw:py-3 tw:font-mono tw:text-[13px] tw:tabular-nums tw:whitespace-nowrap tw:text-right tw:text-ok tw:font-semibold'>+{formatCurrency(bucket.debtDuesTotal, currency)}</td>
																		</tr>
																		{bucket.collectedDuesTotal !== bucket.debtDuesTotal && (
																			<tr>
																				<td className='tw:px-4 tw:py-3 tw:text-[13.5px] tw:text-ink-2'>Cobrado por el propietario (no suma){tag}</td>
																				<td className='tw:px-4 tw:py-3 tw:font-mono tw:text-[13px] tw:tabular-nums tw:whitespace-nowrap tw:text-right tw:text-ink-3'>−{formatCurrency(bucket.debtDuesTotal - bucket.collectedDuesTotal, currency)}</td>
																			</tr>
																		)}
																		<tr>
																			<td className='tw:px-4 tw:py-3 tw:text-[13.5px] tw:text-ink-0'>IRPF ({balanceData.irpf}%){tag}</td>
																			<td className='tw:px-4 tw:py-3 tw:font-mono tw:text-[13px] tw:tabular-nums tw:whitespace-nowrap tw:text-right tw:text-danger'>−{formatCurrency(bucket.irpfDeduction, currency)}</td>
																		</tr>
																		<tr className='tw:font-semibold tw:bg-surface-2'>
																			<td className='tw:px-4 tw:py-3 tw:text-[13.5px]'>Subtotal{tag}</td>
																			<td className='tw:px-4 tw:py-3 tw:font-mono tw:text-[13px] tw:tabular-nums tw:whitespace-nowrap tw:text-right'>{formatCurrency(bucket.collectedDuesTotal - bucket.irpfDeduction, currency)}</td>
																		</tr>
																		{assetCommissionsRaw.filter(c => commissionAppliesToCurrency(c, currency, totalsCurrencies)).map((c) => (
																			<tr key={`${c._id || getCommissionLabel(c)}-${currency}`}>
																				<td className='tw:px-4 tw:py-3 tw:text-[13.5px] tw:text-ink-0'>Comisión: {getCommissionLabel(c)} ({getCommissionRateLabel(c)}){tag}</td>
																				<td className='tw:px-4 tw:py-3 tw:font-mono tw:text-[13px] tw:tabular-nums tw:whitespace-nowrap tw:text-right tw:text-danger'>−{formatCurrency(computeCommissionDeduction(c, bucket.debtDuesTotal, bucket.irpfDeduction), currency)}</td>
																			</tr>
																		))}
																	</React.Fragment>
																);
															})}
															{assetGastoDues.length > 0 && (() => {
																const gastoTotals = {};
																assetGastoDues.forEach(due => {
																	const currency = resolveDueCurrency(due) || balanceData.currency;
																	gastoTotals[currency] = (gastoTotals[currency] || 0) + (due.amount || 0) + (due.surcharge || 0);
																});
																const gastoMulti = Object.keys(gastoTotals).length > 1;
																return (
																	<>
																		{assetGastoDues.map((due, index) => (
																			<tr key={due._id || index}>
																				<td className='tw:px-4 tw:py-3 tw:text-[13.5px] tw:text-ink-0'>
																					{index === 0 && (<strong>Gasto de Activo Fijo</strong>)}
																					<span className={index === 0 ? '' : 'tw:ml-5'} style={{ display: 'inline-block' }}>
																						<span className='tw:font-mono tw:text-[11.5px] tw:text-ink-3'>{due.customId || 'N/A'}</span>
																					</span>
																				</td>
																				<td className='tw:px-4 tw:py-3 tw:font-mono tw:text-[13px] tw:tabular-nums tw:whitespace-nowrap tw:text-right tw:text-danger'>−{formatCurrency((due.amount || 0) + (due.surcharge || 0), resolveDueCurrency(due))}</td>
																			</tr>
																		))}
																		{Object.entries(gastoTotals).map(([currency, total]) => (
																			<tr key={`gasto-${currency}`}>
																				<td className='tw:px-4 tw:py-3 tw:text-[13.5px] tw:font-semibold tw:text-ink-0'>Total Gasto de Activo Fijo{gastoMulti ? ` (${currency})` : ''}</td>
																				<td className='tw:px-4 tw:py-3 tw:font-mono tw:text-[13px] tw:tabular-nums tw:whitespace-nowrap tw:text-right tw:text-danger tw:font-semibold'>−{formatCurrency(total, currency)}</td>
																			</tr>
																		))}
																	</>
																);
															})()}
															<tr>
																<td colSpan="2" className='tw:px-4 tw:pt-[9px] tw:pb-[7px] tw:text-[10.5px] tw:font-bold tw:uppercase tw:tracking-[0.07em] tw:text-ink-3 tw:bg-surface-2'>Gastos</td>
															</tr>
															{assetBills.map((bill, index) => (
																<tr key={bill._id || index}>
																	<td className='tw:pr-4 tw:py-3 tw:pl-[30px] tw:text-[13.5px] tw:text-ink-0'>
																		{bill.name || 'Gasto'}{' '}
																		<Pill tone={bill.paid ? 'ok' : 'danger'}>{bill.paid ? 'Pagado' : 'Impago'}</Pill>
																		{billChargesTenant(bill) && <span className='tw:ml-1.5'><Pill tone='neutral'>Cargo inquilino</Pill></span>}
																	</td>
																	<td className={classnames('tw:px-4 tw:py-3 tw:font-mono tw:text-[13px] tw:tabular-nums tw:whitespace-nowrap tw:text-right', billChargesTenant(bill) ? 'tw:text-ok' : 'tw:text-danger')}>{billChargesTenant(bill) ? '+' : '−'}{formatCurrency(bill.amount || 0, resolveBillCurrency(bill))}</td>
																</tr>
															))}
															{assetBills.length === 0 && (
																<tr>
																	<td className='tw:pr-4 tw:py-3 tw:pl-[30px] tw:text-[13.5px] tw:text-ink-3'>Sin gastos este mes</td>
																	<td className='tw:px-4 tw:py-3 tw:font-mono tw:text-[13px] tw:tabular-nums tw:whitespace-nowrap tw:text-right tw:text-ink-4'>{formatCurrency(0)}</td>
																</tr>
															)}
															{assetBills.length > 0 && (() => {
																const ownerTotals = {};
																const tenantTotals = {};
																assetBills.forEach(bill => {
																	const currency = resolveBillCurrency(bill) || balanceData.currency;
																	const target = billChargesTenant(bill) ? tenantTotals : ownerTotals;
																	target[currency] = (target[currency] || 0) + (bill.amount || 0);
																});
																const billMulti = new Set([...Object.keys(ownerTotals), ...Object.keys(tenantTotals)]).size > 1;
																return (
																	<>
																		{Object.entries(ownerTotals).map(([currency, total]) => (
																			<tr key={`bills-${currency}`}>
																				<td className='tw:px-4 tw:py-3 tw:text-[13.5px] tw:font-semibold tw:text-ink-0'>Total gastos propietario{billMulti ? ` (${currency})` : ''}</td>
																				<td className='tw:px-4 tw:py-3 tw:font-mono tw:text-[13px] tw:tabular-nums tw:whitespace-nowrap tw:text-right tw:text-danger tw:font-semibold'>−{formatCurrency(total, currency)}</td>
																			</tr>
																		))}
																		{Object.entries(tenantTotals).map(([currency, total]) => (
																			<tr key={`tenant-bills-${currency}`}>
																				<td className='tw:px-4 tw:py-3 tw:text-[13.5px] tw:font-semibold tw:text-ink-0'>Total cargos al inquilino{billMulti ? ` (${currency})` : ''}</td>
																				<td className='tw:px-4 tw:py-3 tw:font-mono tw:text-[13px] tw:tabular-nums tw:whitespace-nowrap tw:text-right tw:text-ok tw:font-semibold'>+{formatCurrency(total, currency)}</td>
																			</tr>
																		))}
																	</>
																);
															})()}
															{totalsCurrencies.map((currency) => {
																const bucket = assetTotals.byCurrency[currency] || { grandTotal: 0 };
																// An individual asset can settle in either direction, so the
																// sign decides the wording and the amount is shown absolute.
																const signed = bucket.grandTotal;
																return (
																	<tr className='tw:bg-surface-2' key={`subtotal-${currency}`}>
																		<td className='tw:px-4 tw:py-3 tw:text-[13.5px] tw:font-semibold tw:text-ink-0'>
																			{saldoLabel(signed)} · {assetLabel}{multiCurrency ? ` (${currency})` : ''}
																		</td>
																		<td className={classnames('tw:px-4 tw:py-3 tw:font-mono tw:text-[14px] tw:tabular-nums tw:whitespace-nowrap tw:text-right tw:font-bold', signed >= 0 ? 'tw:text-ok' : 'tw:text-danger')}>
																			{formatCurrency(Math.abs(signed), currency)}
																		</td>
																	</tr>
																);
															})}
														</tbody>
													);
												});
											})()}
											<tfoot>
												{(balanceData.currencies?.length ? balanceData.currencies : [balanceData.currency]).map((currency) => {
													const bucket = balanceData.byCurrency?.[currency];
													const signed = bucket ? bucket.grandTotal : balanceData.grandTotal;
													const multi = (balanceData.currencies?.length || 1) > 1;
													return (
														<tr className='tw:bg-accent-soft tw:border-t-2 tw:border-accent' key={currency}>
															<td className='tw:px-4 tw:py-3.5 tw:font-bold tw:uppercase tw:tracking-[0.06em] tw:text-[12px] tw:text-accent-ink'>{saldoLabel(signed)}{multi ? ` (${currency})` : ''}</td>
															<td className={classnames('tw:px-4 tw:py-3.5 tw:font-mono tw:font-bold tw:text-[16px] tw:tabular-nums tw:whitespace-nowrap tw:text-right', signed >= 0 ? 'tw:text-ok' : 'tw:text-danger')}>
																{formatCurrency(Math.abs(signed), currency)}
															</td>
														</tr>
													);
												})}
											</tfoot>
										</table>
									</div>
								) : (
								// Other types: single-asset display
									<div className='tw:overflow-x-auto'>
										<table className='tw:w-full tw:border-collapse tw:tabular-nums'>
											<thead>
												<tr>
													<th className='tw:px-3 tw:py-2 tw:text-left tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.06em] tw:text-ink-3 tw:bg-surface-2 tw:border-b tw:border-border'>Concepto</th>
													<th className='tw:px-3 tw:py-2 tw:text-left tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.06em] tw:text-ink-3 tw:bg-surface-2 tw:border-b tw:border-border'>Cuota</th>
													<th className='tw:px-3 tw:py-2 tw:text-right tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.06em] tw:text-ink-3 tw:bg-surface-2 tw:border-b tw:border-border'>Monto</th>
												</tr>
											</thead>
											<tbody>
												{(() => {
													const currencies = balanceData.currencies?.length ? balanceData.currencies : [balanceData.currency];
													const multiCurrency = currencies.length > 1;
													// Currencies that actually have income dues — a currency present
													// only via bills shouldn't get a zero-value "Total Cuotas de
													// Deudas" row.
													const incomeDuesCurrencies = new Set(
														balanceData.debtDues.filter(d => !expenseConcepts.has(d.debtConcept)).map(due => resolveDueCurrency(due) || balanceData.currency)
													);
													return (
														<>
															{Object.keys(balanceData.debtDuesByConcept).filter(concept => !expenseConcepts.has(concept)).map(concept => {
																const dues = balanceData.debtDuesByConcept[concept];
																const conceptTotals = {};
																dues.forEach(due => {
																	const currency = resolveDueCurrency(due) || balanceData.currency;
																	conceptTotals[currency] = (conceptTotals[currency] || 0) + (due.amount || 0) + (due.surcharge || 0);
																});
																const conceptMulti = Object.keys(conceptTotals).length > 1;

																return (
																	<React.Fragment key={concept}>
																		{dues.map((due, index) => (
																			<tr key={due._id || index}>
																				<td className='tw:px-3 tw:py-1.5'>
																					{index === 0 && (
																						<strong>{getConceptLabel(concept)}</strong>
																					)}
																				</td>
																				<td className='tw:px-3 tw:py-1.5 tw:font-mono tw:text-[12px] tw:text-ink-3'>{due.customId || 'N/A'}{!dueIsCollected(due) && <span className='tw:ml-2'><Pill tone='neutral'>Cobra propietario</Pill></span>}</td>
																				<td className={classnames('tw:px-3 tw:py-1.5 tw:text-right', dueIsCollected(due) ? 'tw:text-ok' : 'tw:text-ink-4')}>
																				+{formatCurrency((due.amount || 0) + (due.surcharge || 0), resolveDueCurrency(due))}
																				</td>
																			</tr>
																		))}
																		{Object.entries(conceptTotals).map(([currency, total]) => (
																			<tr className='tw:bg-ok-soft tw:font-semibold' key={`${concept}-${currency}`}>
																				<td colSpan="2" className='tw:px-3 tw:py-1.5'>
																					<em>Subtotal {getConceptLabel(concept)}{conceptMulti ? ` (${currency})` : ''}</em>
																				</td>
																				<td className='tw:px-3 tw:py-1.5 tw:text-ok tw:text-right'>
																				+{formatCurrency(total, currency)}
																				</td>
																			</tr>
																		))}
																	</React.Fragment>
																);
															})}
															{balanceData.debtDues.filter(d => !expenseConcepts.has(d.debtConcept)).length === 0 && (
																<tr>
																	<td colSpan="3" className='tw:px-3 tw:py-6 tw:text-center tw:text-ink-3'>
																	No hay cuotas de deudas para este mes
																	</td>
																</tr>
															)}
															{currencies.filter(currency => incomeDuesCurrencies.has(currency) || (balanceData.byCurrency?.[currency]?.commissionsTotal || 0) > 0).map((currency) => {
																const bucket = balanceData.byCurrency?.[currency];
																if (!bucket) return null;
																const tag = multiCurrency ? ` (${currency})` : '';
																return (
																	<React.Fragment key={currency}>
																		<tr className='tw:font-semibold tw:bg-ok-soft'>
																			<td colSpan="2" className='tw:px-3 tw:py-1.5'>Total Cuotas de Deudas{tag}</td>
																			<td className='tw:px-3 tw:py-1.5 tw:text-ok tw:text-right'>+{formatCurrency(bucket.debtDuesTotal, currency)}</td>
																		</tr>
																		{bucket.collectedDuesTotal !== bucket.debtDuesTotal && (
																			<tr>
																				<td colSpan="2" className='tw:px-3 tw:py-1.5 tw:text-ink-2'>Cobrado por el propietario (no suma){tag}</td>
																				<td className='tw:px-3 tw:py-1.5 tw:text-ink-3 tw:text-right'>-{formatCurrency(bucket.debtDuesTotal - bucket.collectedDuesTotal, currency)}</td>
																			</tr>
																		)}
																		<tr>
																			<td colSpan="2" className='tw:px-3 tw:py-1.5'>IRPF ({balanceData.irpf}%){tag}</td>
																			<td className='tw:px-3 tw:py-1.5 tw:text-danger tw:text-right'>-{formatCurrency(bucket.irpfDeduction, currency)}</td>
																		</tr>
																		<tr className='tw:font-semibold tw:bg-surface-2'>
																			<td colSpan="2" className='tw:px-3 tw:py-1.5'>Subtotal{tag}</td>
																			<td className='tw:px-3 tw:py-1.5 tw:text-right'>{formatCurrency(bucket.collectedDuesTotal - bucket.irpfDeduction, currency)}</td>
																		</tr>
																		{(balanceData.commissions || []).filter(c => commissionAppliesToCurrency(c, currency, currencies)).map((c) => (
																			<tr key={`${c._id || getCommissionLabel(c)}-${currency}`}>
																				<td colSpan="2" className='tw:px-3 tw:py-1.5'>Comisión: {getCommissionLabel(c)} ({getCommissionRateLabel(c)}){tag}</td>
																				<td className='tw:px-3 tw:py-1.5 tw:text-danger tw:text-right'>-{formatCurrency(computeCommissionDeduction(c, bucket.debtDuesTotal, bucket.irpfDeduction), currency)}</td>
																			</tr>
																		))}
																	</React.Fragment>
																);
															})}
															{(balanceData.gastoDues || []).length > 0 && (() => {
																const gastoTotals = {};
																(balanceData.gastoDues || []).forEach(due => {
																	const currency = resolveDueCurrency(due) || balanceData.currency;
																	gastoTotals[currency] = (gastoTotals[currency] || 0) + (due.amount || 0) + (due.surcharge || 0);
																});
																const gastoMulti = Object.keys(gastoTotals).length > 1;
																return (
																	<>
																		{(balanceData.gastoDues || []).map((due, index) => (
																			<tr key={due._id || index}>
																				<td className='tw:px-3 tw:py-1.5'>{index === 0 ? <strong>Gasto de Activo Fijo</strong> : ''}</td>
																				<td className='tw:px-3 tw:py-1.5 tw:font-mono tw:text-[12px] tw:text-ink-3'>{due.customId || 'N/A'}</td>
																				<td className='tw:px-3 tw:py-1.5 tw:text-danger tw:text-right'>-{formatCurrency((due.amount || 0) + (due.surcharge || 0), resolveDueCurrency(due))}</td>
																			</tr>
																		))}
																		{Object.entries(gastoTotals).map(([currency, total]) => (
																			<tr className='tw:font-semibold tw:bg-danger-soft' key={`gasto-${currency}`}>
																				<td colSpan="2" className='tw:px-3 tw:py-1.5'>Total Gasto de Activo Fijo{gastoMulti ? ` (${currency})` : ''}</td>
																				<td className='tw:px-3 tw:py-1.5 tw:text-danger tw:text-right'>-{formatCurrency(total, currency)}</td>
																			</tr>
																		))}
																	</>
																);
															})()}
															{balanceData.bills.map((bill, index) => (
																<tr key={bill._id || index}>
																	<td colSpan="2" className='tw:px-3 tw:py-1.5'>
																		{bill.name || 'Gasto'}{' '}
																		<Pill tone={bill.paid ? 'ok' : 'danger'}>{bill.paid ? 'Pagado' : 'Impago'}</Pill>
																		{billChargesTenant(bill) && <span className='tw:ml-1.5'><Pill tone='neutral'>Cargo inquilino</Pill></span>}
																	</td>
																	<td className={classnames('tw:px-3 tw:py-1.5 tw:text-right', billChargesTenant(bill) ? 'tw:text-ok' : 'tw:text-danger')}>{billChargesTenant(bill) ? '+' : '-'}{formatCurrency(bill.amount || 0, resolveBillCurrency(bill))}</td>
																</tr>
															))}
															{balanceData.bills.length === 0 && (
																<tr>
																	<td colSpan="2" className='tw:px-3 tw:py-1.5 tw:text-ink-3'>Gastos</td>
																	<td className='tw:px-3 tw:py-1.5 tw:text-danger tw:text-right'>-{formatCurrency(0)}</td>
																</tr>
															)}
															{balanceData.bills.length > 0 && (() => {
																const ownerTotals = {};
																const tenantTotals = {};
																balanceData.bills.forEach(bill => {
																	const currency = resolveBillCurrency(bill) || balanceData.currency;
																	const target = billChargesTenant(bill) ? tenantTotals : ownerTotals;
																	target[currency] = (target[currency] || 0) + (bill.amount || 0);
																});
																const billMulti = new Set([...Object.keys(ownerTotals), ...Object.keys(tenantTotals)]).size > 1;
																return (
																	<>
																		{Object.entries(ownerTotals).map(([currency, total]) => (
																			<tr className='tw:font-semibold tw:bg-danger-soft' key={`bills-${currency}`}>
																				<td colSpan="2" className='tw:px-3 tw:py-1.5'>Total gastos propietario{billMulti ? ` (${currency})` : ''}</td>
																				<td className='tw:px-3 tw:py-1.5 tw:text-danger tw:text-right'>-{formatCurrency(total, currency)}</td>
																			</tr>
																		))}
																		{Object.entries(tenantTotals).map(([currency, total]) => (
																			<tr className='tw:font-semibold tw:bg-ok-soft' key={`tenant-bills-${currency}`}>
																				<td colSpan="2" className='tw:px-3 tw:py-1.5'>Total cargos al inquilino{billMulti ? ` (${currency})` : ''}</td>
																				<td className='tw:px-3 tw:py-1.5 tw:text-ok tw:text-right'>+{formatCurrency(total, currency)}</td>
																			</tr>
																		))}
																	</>
																);
															})()}
														</>
													);
												})()}
											</tbody>
											<tfoot>
												{(balanceData.currencies?.length ? balanceData.currencies : [balanceData.currency]).map((currency) => {
													const bucket = balanceData.byCurrency?.[currency];
													const grandTotal = bucket ? bucket.grandTotal : balanceData.grandTotal;
													const multi = (balanceData.currencies?.length || 1) > 1;
													return (
														<tr className='tw:bg-accent-soft tw:font-semibold tw:text-[1.2em] tw:border-t-2 tw:border-accent' key={currency}>
															<td colSpan="2" className='tw:px-3 tw:py-3.5'>{saldoLabel(grandTotal)}{multi ? ` (${currency})` : ''}</td>
															<td className={classnames('tw:px-3 tw:py-3.5 tw:text-right tw:text-[1.3em]', grandTotal >= 0 ? 'tw:text-ok' : 'tw:text-danger')}>
																{formatCurrency(Math.abs(grandTotal), currency)}
															</td>
														</tr>
													);
												})}
											</tfoot>
										</table>
									</div>
								)}
							</>
						) : null}
					</div>
				</div>
			</div>
		</>
	);
}

export default Balance;
